import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store/appStore';
import { useItemNavigation } from './useItemNavigation';
import { parseUrl, buildUrl, type UrlState } from '@/utils/urlParsing';
import { handleError } from '@/utils/errorHandler';

// Rotte fuori dal sistema di viste dei progetti, che gestiscono la propria
// navigazione. Quando si è su queste, non si azzera lo store (primo effetto) e
// non si riscrive l'URL (secondo effetto), a meno che l'utente non se ne vada
// esplicitamente dalla barra laterale.
// Sta fuori dal componente perché è una costante: dentro verrebbe ricreata a
// ogni render e non potrebbe stare nelle dipendenze degli effetti.
const STANDALONE_ROUTES = ['/team/calendar', '/profile/'];

/**
* Hook per sincronizzare React Router con lo store dell'app.
* Gestisce la navigazione bidirezionale tra URL e stato dell'app:
* - Quando cambia l'URL, aggiorna lo store
* - Quando cambia lo store, aggiorna l'URL
* 
* Previene loop infiniti usando flag di sincronizzazione (isUpdatingFromUrl, isUpdatingFromStore).
* Gestisce anche la navigazione degli item (documenti, chat, etc.) quando presente nell'URL.
* 
* ⚠️ OPTIMIZED: Usa selector specifici per evitare re-render non necessari.
* Ogni selector crea una subscription separata, quindi solo i valori effettivamente cambiati
* causano re-render del componente.
* 
* @note Per ottimizzazioni future, considerare selector combinati per valori che cambiano insieme
* (es. tutti i filtri insieme) per ridurre il numero di subscription.
*/
export function useRouterSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const viewMode = useAppStore((state) => state.viewMode);
  const filterPath = useAppStore((state) => state.filters.path);
  const selectedTaskId = useAppStore((state) => state.selectedTaskId);
  const currentItemType = useAppStore((state) => state.currentItemType);
  const isLoadingItem = useAppStore((state) => state.isLoadingItem);
  const currentDocument = useAppStore((state) => state.currentDocument);
  const currentChat = useAppStore((state) => state.currentChat);
  const currentVoiceChannel = useAppStore((state) => state.currentVoiceChannel);
  const currentSpreadsheet = useAppStore((state) => state.currentSpreadsheet);
  const currentPresentation = useAppStore((state) => state.currentPresentation);
  const deviationGroupBy = useAppStore((state) => state.deviationGroupBy);
  const deviationFilter = useAppStore((state) => state.deviationFilter);
  const deviationAssignee = useAppStore((state) => state.deviationAssignee);
  const handleUrlChange = useAppStore((state) => state.handleUrlChange);
  const closeCurrentItem = useAppStore((state) => state.closeCurrentItem);
  const setIsLoadingItem = useAppStore((state) => state.setIsLoadingItem);

  const { handleItemClick } = useItemNavigation();
  const isUpdatingFromUrl = useRef(false);
  const isUpdatingFromStore = useRef(false);
  const isLoadingItemRef = useRef(false);
  const previousLocation = useRef(location.pathname + location.search);
  const hasSyncedOnce = useRef(false);

  const standaloneEntryViewMode = useRef<string | null>(null);

  // Sincronizza lo store quando cambia l'URL
  useEffect(() => {
  const currentLocation = location.pathname + location.search;

  // Skip if we just updated from store
  if (isUpdatingFromStore.current) {
      isUpdatingFromStore.current = false;
      previousLocation.current = currentLocation;
      return;
  }

  // Skip if location hasn't actually changed.
  // Non al primo giro, però: al montaggio previousLocation contiene già
  // l'URL corrente, quindi senza questa eccezione arrivare direttamente su
  // /projects/:path/documents/:id (link condiviso, ricarica della pagina,
  // segnalibro) non caricava mai l'elemento e lasciava la pagina bianca.
  if (hasSyncedOnce.current && previousLocation.current === currentLocation) {
      return;
  }
  hasSyncedOnce.current = true;

  previousLocation.current = currentLocation;

  if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      return;
  }

  // Skip store sync for standalone routes (profile, team/calendar).
  // These routes aren't part of the project view system and shouldn't reset the store.
  const isStandalone = STANDALONE_ROUTES.some(r => location.pathname.startsWith(r));
  if (isStandalone) {
      if (standaloneEntryViewMode.current === null) {
        // Letto dallo store invece che dalla variabile del render: qui serve
        // solo fotografare il valore corrente all'ingresso nella rotta, e
        // metterlo fra le dipendenze rilancerebbe la sincronizzazione a ogni
        // cambio di vista.
        standaloneEntryViewMode.current = useAppStore.getState().viewMode;
      }
      return;
  }
  standaloneEntryViewMode.current = null;

  // Parse URL using shared utility
  const urlState = parseUrl();

  // Aggiorna lo store
  handleUrlChange(urlState);

  // Gestisci la navigazione degli item
  if (urlState.itemType && urlState.itemId) {
      // Set loading ref immediately to prevent URL sync from redirecting
      isLoadingItemRef.current = true;
      setIsLoadingItem(true);
      handleItemClick(urlState.itemType, urlState.itemId, urlState.path)
        .finally(() => {
          isLoadingItemRef.current = false;
        })
        .catch((err) => handleError(err, { level: 'warning', message: 'Errore nella navigazione dell\'elemento' }));
  } else {
      isLoadingItemRef.current = false;
      closeCurrentItem();
  }
  }, [location.pathname, location.search, handleUrlChange, closeCurrentItem, handleItemClick, setIsLoadingItem]);

  // Sincronizza l'URL quando cambia lo store
  useEffect(() => {
  // Don't update URL while an item is loading to prevent premature redirects
  // Use ref for immediate check (state might not be updated yet in same render cycle)
  if (isLoadingItem || isLoadingItemRef.current) {
      return;
  }

  // Skip URL sync for standalone routes, UNLESS the user changed viewMode via sidebar.
  // standaloneEntryViewMode captures what viewMode was when entering the standalone route.
  // If it hasn't changed, the user is just browsing the standalone page → skip.
  // If it changed, the user clicked a sidebar link → clear ref and navigate away.
  if (STANDALONE_ROUTES.some((r) => location.pathname.startsWith(r))) {
      if (standaloneEntryViewMode.current !== null && viewMode === standaloneEntryViewMode.current) {
        return;
      }
      standaloneEntryViewMode.current = null;
  }

  const currentUrlState = parseUrl();

  // Build URL state from app state first to check if URL needs updating
  const currentItemId =
      currentDocument?.id ||
      currentChat?.id ||
      currentVoiceChannel?.id ||
      currentSpreadsheet?.id ||
      currentPresentation?.id ||
      null;

  const urlState: UrlState = {
      viewMode,
      path: filterPath,
      taskId: selectedTaskId,
      itemType: currentItemType,
      itemId: currentItemId,
      deviationGroupBy,
      deviationFilter,
      deviationAssignee,
  };

  const newPath = buildUrl(urlState);
  const currentPath = location.pathname + location.search;

  // If current URL has an item but store doesn't have that item loaded yet,
  // don't navigate away - the item is still loading.
  // However, if the path has changed (user navigated to a different space/list),
  // allow the URL update so we don't get stuck on a blank item view.
  if (currentUrlState.itemType && currentUrlState.itemId && !currentItemId && currentUrlState.path === filterPath) {
      return;
  }

  // If URL already matches, reset flags and return early
  if (currentPath === newPath) {
      isUpdatingFromUrl.current = false;
      isUpdatingFromStore.current = false;
      return;
  }

  // If store changed but URL doesn't match, reset the flag to allow navigation
  // This handles the case where user clicks rapidly between views
  // The flag might be true from a previous navigation that hasn't completed yet
  if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
  }

  isUpdatingFromStore.current = true;
  isUpdatingFromUrl.current = true;
  navigate(newPath, { replace: true });
  }, [
  viewMode,
  filterPath,
  selectedTaskId,
  currentItemType,
  isLoadingItem,
  currentDocument,
  currentChat,
  currentVoiceChannel,
  currentSpreadsheet,
  currentPresentation,
  deviationGroupBy,
  deviationFilter,
  deviationAssignee,
  navigate,
  location.pathname,
  location.search,
  ]);
}
